import React, { useEffect, useState } from "react";
import { Box, lighten, Typography, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { format } from "date-fns";
import { Message, MessageTypeColor } from "../../../../types";
import { ReactionAndFavorite } from "../reaction-and-favorite/reaction-and-favorite";
import { CustomMarkdown } from "../../../custom-markdown/custom-markdown";
import { useAppStore } from "../../../../stores/appStore";
import { useChatStore } from "../../../../stores/chatStore";
import { useTextMessageStyles } from "./styles";
import { useTheme } from "@mui/material/styles";
import { getFlashCardMobileContainerStyles } from "../../../mmk-section/styles";
import { motion } from "framer-motion";

interface FlashCard {
  title?: string;
  text?: string;
  subtitle?: string;
  title_step?: string;
  custom_background?: boolean;
}

type FlashCardSet = FlashCard[];
type NestedFlashCards = FlashCardSet[];

interface TextMessageProps {
  message: Message;
  isBot: boolean;
  variant: MessageTypeColor;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;

  nextMessage?: Message;
  setLargeHigth: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveFlashCardText: React.Dispatch<React.SetStateAction<string>>;
  // When true, hide ReactionAndFavorite and timestamp (used inside TabbedMessage)
  embedded?: boolean;
}

export const FlashCardMessage: React.FC<TextMessageProps> = ({
  message,
  isBot,
  variant,
  currentReaction,
  isSaved,
  markedIds,
  isTags,
  handleReactionClick,
  handleAddToFavorite,
  nextMessage,
  setLargeHigth,
  handleRemoveTag,
  setActiveFlashCardText,
  embedded = false,
}) => {
  const { isDark } = useAppStore();
  const { setTargetMessageId } = useChatStore();
  const styles = useTextMessageStyles(isBot, variant);
  const theme = useTheme();

  const [, setLargeHigthState] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    nextMessage ? false : !!message.collapse
  );
  const [currentCardSetIndex, setCurrentCardSetIndex] = useState(0);

  useEffect(() => {
    if (!nextMessage) {
      setIsCollapsed(false);
    } else if (message.collapse !== undefined) {
      setIsCollapsed(!message.collapse);
    }
  }, [message.collapse, nextMessage]);

  useEffect(() => {
    let currentSet: FlashCard[] = [];
    const isNestedStructure =
      Array.isArray(message.flash_card) &&
      message.flash_card.length > 0 &&
      Array.isArray(message.flash_card[0]);

    if (isNestedStructure && message.flash_card) {
      const nestedCards = message.flash_card as unknown as NestedFlashCards;
      currentSet = nestedCards[currentCardSetIndex];
    } else {
      currentSet = message.flash_card as unknown as FlashCardSet;
    }

    const combinedText = currentSet.reduce(
      (acc, card) => acc + (card.text || ""),
      ""
    );

    setActiveFlashCardText(combinedText);
  }, [message.flash_card, currentCardSetIndex, setActiveFlashCardText]);

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
    setLargeHigthState((prev) => !prev);
    setLargeHigth((prev: boolean) => !prev);
  };

  if (!message.flash_card) {
    return null;
  }

  const isNestedStructure =
    Array.isArray(message.flash_card) &&
    message.flash_card.length > 0 &&
    Array.isArray(message.flash_card[0]);

  let currentCardSet: FlashCardSet;
  if (isNestedStructure) {
    const nestedCards = message.flash_card as unknown as NestedFlashCards;
    currentCardSet = nestedCards[currentCardSetIndex];
  } else {
    currentCardSet = message.flash_card as unknown as FlashCardSet;
  }

  const getFilteredFlashCards = () => {
    if (!isCollapsed) {
      return currentCardSet;
    }
    let cumulativeTextLength = 0;
    const filteredCards: FlashCard[] = [];
    for (let i = 0; i < currentCardSet.length; i++) {
      const card = currentCardSet[i];
      const textLength = card.text ? card.text.length : 0;
      if (filteredCards.length === 0) {
        filteredCards.push(card);
        cumulativeTextLength += textLength;
      } else if (cumulativeTextLength + textLength <= 300) {
        filteredCards.push(card);
        cumulativeTextLength += textLength;
      } else {
        break;
      }
    }
    return filteredCards;
  };

  const flashCardsToRender = getFilteredFlashCards();

  const totalTextLength = currentCardSet.reduce(
    (acc: number, card: FlashCard) => {
      return acc + (card.text ? card.text.length : 0);
    },
    0
  );

  const shouldCollapse = totalTextLength > 300;

  const customMarkdownStyle = {
    wordBreak: "break-word",
    "& ul": {
      margin: 0,
      marginBottom: "1em",
    },
  };

  const adjustedBgLighten =
    styles.container.background !== "inherit" &&
    !styles.container.background.startsWith("linear-gradient")
      ? lighten(styles.container.background, 0.2)
      : styles.container.background;

  const handleNextCardSet = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isNestedStructure && message.flash_card) {
      const nestedCards = message.flash_card as unknown as NestedFlashCards;
      setCurrentCardSetIndex((prev) => (prev + 1) % nestedCards.length);
    }
  };

  const handlePrevCardSet = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isNestedStructure && message.flash_card) {
      const nestedCards = message.flash_card as unknown as NestedFlashCards;
      setCurrentCardSetIndex(
        (prev) => (prev - 1 + nestedCards.length) % nestedCards.length
      );
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0.8, scale: 0.96 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.95 },
    },
  };

  return (
    <Box
      sx={{
        ...styles.container,
        ...getFlashCardMobileContainerStyles(theme, embedded),
      }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ width: "100%" } as React.CSSProperties}
      >
        {flashCardsToRender.map((card: FlashCard, index: number) => {
          const { title, text, custom_background, subtitle, title_step } = card;
          const displayText =
            index === 0 && text && isCollapsed && text.length > 300
              ? `${text.substring(0, 300)}...`
              : text;

          const combinedMarkdown =
            subtitle && subtitle !== "undefined" && subtitle === "Q:"
              ? `<span style="white-space: nowrap; color: red; margin-right: 3px; font-weight: bold;">${subtitle}</span>${displayText}`
              : displayText;
          const combinedTitle = title_step
            ? `<span style="white-space: nowrap; font-size: 12px; font-weight: bold; padding-right: 0.5rem;">${title_step}</span>${title?.replace(/(APPLICATION|CONCEPT|SUMMARY)/g, '<span style="padding-right: 0.4rem">$1</span>')}`
            : title?.replace(
                /(APPLICATION|CONCEPT|SUMMARY)/g,
                '<span style="padding-right: 0.1rem">$1</span>'
              );

          return (
            <motion.div
              key={
                index +
                (card.subtitle || "") +
                (card.text || "") +
                (card.title || "")
              }
              variants={cardVariants}
              initial="hidden"
              animate="visible"
            >
              <Box sx={styles.containerMessage}>
                {title && (
                  <Box sx={styles.titleRow}>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: {
                          xs: "0.9375rem",
                          sm: "1.125rem",
                          md: "1.1875rem",
                        },
                        // CustomMarkdown wraps text into <p>, which makes pill expand full width.
                        // Force paragraph to be inline so fit-content works as expected.
                        "& p": {
                          m: 0,
                          display: "inline",
                        },
                      }}
                    >
                      <CustomMarkdown children={combinedTitle} />
                    </Typography>
                  </Box>
                )}

                {text && (
                  <Box
                    sx={{
                      ...styles.messageRow,
                      ...(custom_background &&
                        !isDark && { background: "#fff" }),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        ...customMarkdownStyle,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "baseline" }}>
                        <CustomMarkdown children={combinedMarkdown} />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </motion.div>
          );
        })}

        {shouldCollapse && (
          <Box
            onClick={handleToggleCollapse}
            sx={{
              mt: 1,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body2">
              {isCollapsed ? "More" : "Less"}
            </Typography>
            <ExpandMoreIcon sx={styles.collapseToggleIcon?.(isCollapsed)} />
          </Box>
        )}

        {message.reply && (
          <Box
            onClick={() =>
              setTargetMessageId(
                message.reply_message_id || null,
                message.reply_message_tab_id || null,
              )
            }
            sx={{
              position: "relative",
              background: adjustedBgLighten,
              alignItems: "stretch",
              borderTopRightRadius: "10px",
              borderBottomRightRadius: "10px",
              borderTopLeftRadius: "5px",
              borderBottomLeftRadius: "5px",
              mb: 1,
              cursor: "pointer",
              pl: "12px",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "6px",
                background: "#ffffff",
                borderTopLeftRadius: "10px",
                borderBottomLeftRadius: "10px",
              }}
            />
            <Typography sx={{ padding: "10px", flex: 1 }}>
              <Box
                sx={{
                  ...customMarkdownStyle,
                  ...(isCollapsed && {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }),
                }}
              >
                <CustomMarkdown children={message.reply} />
              </Box>
            </Typography>
          </Box>
        )}

        {isNestedStructure && message.flash_card && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <IconButton onClick={handlePrevCardSet} size="small" sx={{ mr: 1 }}>
              <NavigateBeforeIcon />
            </IconButton>
            <Typography variant="body2" sx={{ alignSelf: "center" }}>
              {currentCardSetIndex + 1} /{" "}
              {(message.flash_card as unknown as NestedFlashCards).length}
            </Typography>
            <IconButton onClick={handleNextCardSet} size="small" sx={{ ml: 1 }}>
              <NavigateNextIcon />
            </IconButton>
          </Box>
        )}

        {!embedded && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <ReactionAndFavorite
              currentReaction={currentReaction}
              markedIds={markedIds}
              isSaved={isSaved}
              isTags={isTags}
              handleReactionClick={handleReactionClick}
              handleAddToFavorite={handleAddToFavorite}
              handleRemoveTag={handleRemoveTag}
              reactionBg={styles.container.background}
            />

            <Typography variant="caption" sx={styles.timestamp}>
              <span>{format(new Date(message.date), "dd/MM/yy")}</span>
              <span style={{ margin: "0 5px" }} />
              <span>{format(new Date(message.date), "HH:mm")}</span>
            </Typography>
          </Box>
        )}
      </motion.div>
    </Box>
  );
};
